import {
  type AverageTraitConfig,
  type Column,
  ColumnType,
  type CountDedupedTraitConfig,
  type CountTraitConfig,
  type FormulaTraitConfig,
  isMergedColumn,
  type ListTraitConfig,
  type OrderDedupedTraitConfig,
  type RawColumn,
  type RawSqlTraitConfig,
  REALTIME_FORMULA_OPERAND_TYPES,
  type RealtimeFormulaTraitConfig,
  realtimeFormulaOperandIds,
  REALTIME_TRAIT_STREAM_ONLY_MAX_WINDOW_SECONDS,
  type RealtimeTraitConfig,
  realtimeTraitWindowRequiresWarehouseBacking,
  realtimeTraitWindowSeconds,
  type RelatedColumn,
  type SumTraitConfig,
  type TraitCondition,
  type TraitConfig,
  TraitType,
} from "@hightouch/lib/query/visual/types";
import { bigqueryDisallowedIdentifierCharactersRegex } from "@hightouch/lib/query/warehouse-specific/bigquery";
import { getColumnAlias as getColumnAliasBase } from "@hightouch/lib/query/visual/column-alias";

import type { Completion, CompletionSection } from "@codemirror/autocomplete";
import { sha256 } from "js-sha256";
import * as Yup from "yup";

import { isRealtimeEventModel } from "src/utils/event-models";
import {
  type FilterableColumn,
  type TraitDefinition,
  getInitialTraitColumn as transformTraitColumnToRelatedColumn,
} from "src/types/visual";
import { isNumericString } from "src/utils/string";
import {
  getTraitPropertyType,
  type MinimalTraitDefinition,
} from "src/components/explore/visual/utils";
import { isColumnReferenceEqual } from "src/components/explore/filter-popover/utils";
import capitalize from "lodash/capitalize";
import isEqual from "lodash/isEqual";
import uniqWith from "lodash/uniqWith";
import { PredictorType } from "@hightouch/lib/predictive-traits/types";

export { PredictorType };

export enum CalculationMethod {
  Aggregation = "aggregation",
  Count = "count",
  Occurrence = "occurrence",
  Sql = "sql",
  Formula = "formula",
  List = "list",
  Predictive = "prediction",
  CatalogRecommendation = "catalogRecommendation",
  LookalikeAudience = "lookalikeAudience",
  TrackedAudienceMembership = "trackedAudienceMembership",
  RealtimeFormula = "realtimeFormula",
}

export const CALCULATION_METHODS = {
  [CalculationMethod.Aggregation]: {
    label: "Aggregation",
    value: CalculationMethod.Aggregation,
    description: "Calculate the sum or average",
    examples: [
      "Sum of all customer order values",
      "Average user session length",
    ],
  },
  [CalculationMethod.Count]: {
    label: "Count",
    value: CalculationMethod.Count,
    description: "The number of occurrences",
    examples: ["Orders completed", "Page views", "User sessions"],
  },
  [CalculationMethod.Occurrence]: {
    label: "Occurrence",
    value: CalculationMethod.Occurrence,
    description: "Determine the first, last, most or least frequent",
    examples: [
      "First song listened to",
      "Last product viewed",
      "Most frequent product viewed",
      "Least frequent user login",
    ],
  },
  [CalculationMethod.List]: {
    label: "List",
    value: CalculationMethod.List,
    description: "Aggregate a list of values from related or event models",
    examples: [
      "List of last 5 products viewed",
      "List of unique coupon codes used",
    ],
  },
  [CalculationMethod.Sql]: {
    label: "SQL Aggregation",
    value: CalculationMethod.Sql,
    description:
      "Aggregate across related events or entities (sums, averages, counts, arrays, etc)",
    examples: [
      "Building a JSON object",
      "Building a list",
      "Grabbing a specific field out of a JSON object",
      "Custom aggregation",
    ],
  },
  [CalculationMethod.Formula]: {
    label: "SQL Formula",
    value: CalculationMethod.Formula,
    description: "Calculate based on properties and traits with SQL",
    examples: [
      "Using LTV to classify customer loyalty tiers",
      'Creates a "true" string if the user meets a series of criteria',
    ],
  },
  [CalculationMethod.RealtimeFormula]: {
    label: "Realtime formula",
    value: CalculationMethod.RealtimeFormula,
    description: "Combine realtime traits with arithmetic",
    examples: [
      "Average order value from realtime spend and order count",
      "Purchases per page view in the last day",
    ],
  },
  [CalculationMethod.Predictive]: {
    label: "Prediction",
    value: CalculationMethod.Predictive,
    description:
      "Estimate the likelihood of a future action using machine learning. To create a prediction, you’ll need to define your predictive input in the selected parent model first.",
    examples: [
      "Likelihood to purchase in the next 7 days",
      "Probability of churn within 30 days",
      "Chance a user will subscribe this month",
      "Likelihood to return after a trial ends",
    ],
  },
  [CalculationMethod.CatalogRecommendation]: {
    label: "Catalog Recommendation",
    value: CalculationMethod.CatalogRecommendation,
    description:
      "Give personalized recommendations to users based on their behavior. To create a recommendation, you’ll need to enable recommendations on a catalog first.",
    examples: [
      "Suggested products",
      "Personalized offers",
      "Top items based on activity",
    ],
  },
  [CalculationMethod.LookalikeAudience]: {
    label: "Lookalike Audience",
    value: CalculationMethod.LookalikeAudience,
    description:
      "Find users that mimic an existing audience. To create a lookalike audience, you'll need to define your predictive input in the selected parent model first.",
    examples: [
      "Find new potential customers in a new market",
      "Target users who share similar demographics, interests, and behaviors",
    ],
  },
  [CalculationMethod.TrackedAudienceMembership]: {
    label: "Audience membership",
    value: CalculationMethod.TrackedAudienceMembership,
    description:
      "Determine which audiences each user currently belongs to. Useful for syncing audience IDs alongside other traits.",
    examples: [
      "Sync a user's active loyalty tier audiences",
      "List the winback-eligible audiences a user is in",
    ],
  },
};

export const CALCULATION_METHODS_WITH_OBJECT_TRAITS: typeof CALCULATION_METHODS =
  {
    ...CALCULATION_METHODS,
    [CalculationMethod.List]: {
      label: "List",
      value: CalculationMethod.List,
      description:
        "Aggregate a list of values or objects from related or event models",
      examples: [
        "List of last 5 products viewed",
        "List of unique coupon codes used",
        "List of objects containing license type and license expiration date",
      ],
    },
  };

export const traitTypeToCalculationMethod = {
  [TraitType.Sum]: CalculationMethod.Aggregation,
  [TraitType.Average]: CalculationMethod.Aggregation,
  [TraitType.Count]: CalculationMethod.Count,
  [TraitType.First]: CalculationMethod.Occurrence,
  [TraitType.Last]: CalculationMethod.Occurrence,
  [TraitType.LeastFrequent]: CalculationMethod.Occurrence,
  [TraitType.MostFrequent]: CalculationMethod.Occurrence,
  [TraitType.RawSql]: CalculationMethod.Sql,
  [TraitType.Formula]: CalculationMethod.Formula,
  [TraitType.List]: CalculationMethod.List,
  [TraitType.Predictive]: CalculationMethod.Predictive,
  [TraitType.TrackedAudienceMembership]:
    CalculationMethod.TrackedAudienceMembership,
  [TraitType.RealtimeFormula]: CalculationMethod.RealtimeFormula,
};

export const defaultTypeByCalculationMethod = {
  [CalculationMethod.Aggregation]: TraitType.Sum,
  [CalculationMethod.Count]: TraitType.Count,
  [CalculationMethod.Occurrence]: TraitType.First,
  [CalculationMethod.Sql]: TraitType.RawSql,
  [CalculationMethod.Formula]: TraitType.Formula,
  [CalculationMethod.List]: TraitType.List,
  [CalculationMethod.Predictive]: TraitType.Predictive,
  [CalculationMethod.CatalogRecommendation]: TraitType.Predictive,
  [CalculationMethod.LookalikeAudience]: TraitType.Predictive,
  [CalculationMethod.TrackedAudienceMembership]:
    TraitType.TrackedAudienceMembership,
  [CalculationMethod.RealtimeFormula]: TraitType.RealtimeFormula,
};

export const getCalculationMethodForPredictor = (
  predictor?: {
    type?: string;
    catalog_collection_id?: string | null;
  } | null,
): CalculationMethod => {
  switch (predictor?.type) {
    case PredictorType.LOOKALIKE:
      return CalculationMethod.LookalikeAudience;
    case PredictorType.CATALOG_RECOMMENDATION:
      return CalculationMethod.CatalogRecommendation;
    default:
      return CalculationMethod.Predictive;
  }
};

/**
 * The realtime form fields and save-time validation must agree on this
 * predicate: deriving it without the flag leaves a flag-off rollback
 * demanding a backing the form no longer renders.
 */
export const isRealtimeEventModelRelationship = (
  relationship:
    | {
        to_model: {
          event?: {
            event_model_event_sources?: readonly unknown[] | null;
          } | null;
        };
      }
    | null
    | undefined,
  realtimeTraitsEnabled: boolean | undefined,
): boolean =>
  Boolean(realtimeTraitsEnabled) &&
  isRealtimeEventModel(relationship?.to_model.event);

// The columns each mode must map for the trait to actually be backed — a
// partial backing parses as unbacked on the backend, which would save an
// unservable trait.
const validateRealtimeBacking = (
  type: TraitType,
  rawConfig: TraitConfig,
): boolean => {
  if (type === TraitType.List) {
    // Lists are stream-only; they never carry a baseline.
    return true;
  }
  const realtimeConfig = (rawConfig as { realtimeConfig?: RealtimeTraitConfig })
    .realtimeConfig;
  if (!realtimeTraitWindowRequiresWarehouseBacking(realtimeConfig?.window)) {
    return true;
  }
  const backing = realtimeConfig?.warehouseBacking;
  const isOccurrence = type === TraitType.First || type === TraitType.Last;
  if (backing?.mode === "column") {
    // Precomputed columns are lifetime-only: a sliding window's semantics
    // live inside the customer's SQL where the sync can't cut or verify them.
    // A precomputed average cannot be merged with the stream at all.
    if (
      realtimeConfig?.window?.type === "sliding" ||
      type === TraitType.Average
    ) {
      return false;
    }
    return (
      backing.modelId != null &&
      backing.selectColumn != null &&
      (!isOccurrence || backing.orderByColumn != null)
    );
  }
  if (backing?.mode === "events") {
    if (backing.relationshipId == null) {
      return false;
    }
    if (type === TraitType.Sum || type === TraitType.Average) {
      return backing.selectColumn != null;
    }
    if (isOccurrence) {
      return backing.selectColumn != null && backing.orderByColumn != null;
    }
    return true; // Count aggregates COUNT(*): no columns to map.
  }
  return false;
};

// Returns true if validation passes
export const validateConfig = (
  type: TraitType | undefined,
  rawConfig: TraitConfig | undefined,
  opts: { isRealtimeEventRelationship: boolean },
): boolean => {
  if (type == undefined || rawConfig == undefined) {
    return false;
  }

  if (
    opts.isRealtimeEventRelationship &&
    !validateRealtimeBacking(type, rawConfig)
  ) {
    return false;
  }

  switch (type) {
    case TraitType.Sum:
      return (rawConfig as SumTraitConfig).column != undefined;
    case TraitType.Average:
      return (rawConfig as AverageTraitConfig).column != undefined;
    case TraitType.Count:
      return true;
    case TraitType.MostFrequent:
    case TraitType.LeastFrequent: {
      return (rawConfig as CountDedupedTraitConfig).toSelect != undefined;
    }
    case TraitType.First:
    case TraitType.Last: {
      const config = rawConfig as OrderDedupedTraitConfig;
      return config.toSelect != undefined && config.orderBy != undefined;
    }
    case TraitType.RawSql: {
      const config = rawConfig as RawSqlTraitConfig;
      return (
        config.aggregation != undefined && config.resultingType != undefined
      );
    }
    case TraitType.Formula: {
      const config = rawConfig as FormulaTraitConfig;
      return (
        config.transformation !== undefined &&
        config.resultingType !== undefined
      );
    }
    case TraitType.List: {
      const config = rawConfig as ListTraitConfig;
      return (
        ((config.toSelect !== undefined &&
          config.orderBy?.column !== undefined) ||
          config.toSelectList !== undefined) &&
        !(config.slice?.location && config.slice.maxResults == null)
      );
    }
    case TraitType.Predictive: {
      return true;
    }
    case TraitType.TrackedAudienceMembership: {
      // Membership traits are configured through a dedicated flow that submits
      // via `createTrackedAudienceList`, not through the shared trait config
      // shape validated here. Treat the config as always-valid; the resolver
      // is the source of truth for required fields.
      return true;
    }
    case TraitType.RealtimeFormula:
      return (rawConfig as RealtimeFormulaTraitConfig).expression !== undefined;
    default:
      return false;
  }
};

export const handleChangeCalculationMethod = (
  calculationMethod: CalculationMethod,
  form: any,
) => {
  const values = form.getValues();

  form.reset({
    ...values,
    calculation_method: calculationMethod,
    type: defaultTypeByCalculationMethod[calculationMethod],
    config: {},
    relationship_id:
      calculationMethod === CalculationMethod.Formula ||
      calculationMethod === CalculationMethod.RealtimeFormula
        ? null
        : values.relationship_id,
  });
};

export const TRAIT_TYPE_LABELS = {
  [TraitType.Count]: "Count",
  [TraitType.Sum]: "Sum",
  [TraitType.Average]: "Average",
  [TraitType.RawSql]: "Raw SQL",
  [TraitType.LeastFrequent]: "Least frequent",
  [TraitType.MostFrequent]: "Most frequent",
  [TraitType.First]: "First / Min",
  [TraitType.Last]: "Last / Max",
  [TraitType.Formula]: "Formula",
  [TraitType.List]: "List",
  [TraitType.Predictive]: "Prediction",
  [TraitType.TrackedAudienceMembership]: "Audience membership",
  [TraitType.RealtimeFormula]: "Realtime formula",
};

export const TRAIT_TYPE_OPTIONS = Object.keys(TRAIT_TYPE_LABELS)
  .filter(
    (traitType) =>
      // Audience membership traits are only created through the dedicated
      // create-trait wizard flow (they submit via `createTrackedAudienceList`,
      // not the shared trait config shape), so they don't belong in the
      // generic trait-type picker. Gating them out here also keeps them hidden
      // when `trackedAudienceListsEnabled` is off.
      traitType !== TraitType.TrackedAudienceMembership &&
      // Realtime formulas have their own calculation method.
      traitType !== TraitType.RealtimeFormula,
  )
  .map((traitType) => ({
    label: TRAIT_TYPE_LABELS[traitType],
    value: traitType,
  }));

export const INLINE_TRAIT_TYPE_OPTIONS = Object.keys(TRAIT_TYPE_LABELS)
  .filter(
    (traitType) =>
      // RawSql and Formula require writing SQL which isn't great UX
      // with the inline trait interface we have in the audience builder.
      // Therefore, we won't support it here. Users can just create these types
      // of traits using the trait builder.
      traitType !== TraitType.RawSql &&
      traitType !== TraitType.Formula &&
      // List traits require complex configuration (column selection, ordering,
      // slicing) that the inline trait UI does not support.
      traitType !== TraitType.List &&
      // Predictive traits aren't supported via inline traits
      traitType !== TraitType.Predictive &&
      // Audience membership traits are created only through the dedicated
      // create-trait wizard flow, not inline in the audience builder.
      traitType !== TraitType.TrackedAudienceMembership &&
      traitType !== TraitType.RealtimeFormula,
  )
  .map((traitType) => ({
    label: TRAIT_TYPE_LABELS[traitType],
    value: traitType,
  }));

// Calculation methods whose realtime variant the rta-worker can compute.
// Realtime event models only appear in the relationship picker for these
// methods. Sub-type gating (e.g. Occurrence's MostFrequent/LeastFrequent, or
// Aggregation's Average) happens separately, since the sub-type is chosen
// after the relationship.
export const REALTIME_SUPPORTED_CALCULATION_METHODS: ReadonlySet<CalculationMethod> =
  new Set([
    CalculationMethod.Occurrence,
    CalculationMethod.Count,
    CalculationMethod.Aggregation,
  ]);

// Of the Occurrence sub-types, only First and Last are supported in realtime.
// Used to filter the occurrence-type Select once a realtime event model is
// selected as the relationship.
export const REALTIME_SUPPORTED_OCCURRENCE_TYPES: ReadonlySet<TraitType> =
  new Set([TraitType.First, TraitType.Last]);

export const isRealtimeTrait = (trait: {
  type?: string;
  relationship?: {
    to_model?: {
      event?: { event_model_event_sources?: readonly unknown[] | null } | null;
    };
  } | null;
}): boolean =>
  trait.type === TraitType.RealtimeFormula ||
  isRealtimeEventModel(trait.relationship?.to_model?.event);

/** True for the traits the RTA worker accepts as formula operands, numeric realtime traits that aren't formulas. */
export const isRealtimeFormulaOperand = (
  trait: MinimalTraitDefinition & {
    archived_at?: string | null;
    relationship?: {
      to_model?: {
        event?: {
          event_model_event_sources?: readonly unknown[] | null;
        } | null;
        filterable_audience_columns?: FilterableColumn[] | null;
      };
    } | null;
  },
): boolean =>
  !trait.archived_at &&
  REALTIME_FORMULA_OPERAND_TYPES.has(trait.type) &&
  isRealtimeTrait(trait) &&
  NUMBER_TYPES.includes(
    getTraitPropertyType(
      trait,
      trait.relationship?.to_model?.filterable_audience_columns,
    ),
  );

const RTA_LIVE_RESOLVABLE_TRAIT_TYPES: ReadonlySet<string> = new Set([
  TraitType.Count,
  TraitType.Sum,
  TraitType.Average,
  TraitType.First,
  TraitType.Last,
]);

const isRtaLiveResolvableTraitType = (type: string): type is TraitType =>
  RTA_LIVE_RESOLVABLE_TRAIT_TYPES.has(type);

type RtaConditionTrait = {
  type: string;
  config: unknown;
  relationship?: {
    to_model?: {
      event?: {
        event_model_event_sources?: readonly unknown[] | null;
      } | null;
    };
  } | null;
};

/**
 * True when a trait can be referenced in a realtime audience's conditions.
 * A realtime trait needs a supported type, and one that needs a warehouse baseline also needs a complete backing.
 * The worker merges that baseline with the stream, so lifetime and over-24h traits are only complete with it.
 * `warehouseTraitMergeEnabled` is the `rtaWorkerWarehouseTraitMerge` flag: without it the worker never loads baselines.
 */
export const isTraitSupportedInRtaConditions = (
  trait: RtaConditionTrait,
  {
    warehouseTraitMergeEnabled,
    traitsById,
  }: {
    warehouseTraitMergeEnabled: boolean;
    // Needed to check a realtime formula, which resolves only when every operand does.
    traitsById: ReadonlyMap<string, RtaConditionTrait>;
  },
): boolean => {
  if (!isRealtimeTrait(trait)) {
    return true;
  }
  if (trait.type === TraitType.RealtimeFormula) {
    const expression = (trait.config as Partial<RealtimeFormulaTraitConfig>)
      ?.expression;
    if (!expression) {
      return false;
    }
    return realtimeFormulaOperandIds(expression).every((operandId) => {
      const operand = traitsById.get(operandId);
      return (
        operand !== undefined &&
        operand.type !== TraitType.RealtimeFormula &&
        isRealtimeTrait(operand) &&
        isTraitSupportedInRtaConditions(operand, {
          warehouseTraitMergeEnabled,
          traitsById,
        })
      );
    });
  }
  if (!isRtaLiveResolvableTraitType(trait.type)) {
    return false;
  }
  const window = (trait.config as { realtimeConfig?: RealtimeTraitConfig })
    ?.realtimeConfig?.window;
  if (realtimeTraitWindowRequiresWarehouseBacking(window)) {
    return (
      warehouseTraitMergeEnabled &&
      validateRealtimeBacking(trait.type, trait.config as TraitConfig)
    );
  }
  const seconds = realtimeTraitWindowSeconds(window);
  // Excludes a malformed window.
  return (
    typeof seconds === "number" &&
    seconds <= REALTIME_TRAIT_STREAM_ONLY_MAX_WINDOW_SECONDS
  );
};

export const RAW_SQL_COMMON_RES_TYPES = [
  {
    value: ColumnType.Boolean,
    label: "Boolean",
  },
  {
    value: ColumnType.Number,
    label: "Number",
  },
  {
    value: ColumnType.String,
    label: "String",
  },
  {
    value: ColumnType.Timestamp,
    label: "Timestamp",
  },
  {
    value: ColumnType.Date,
    label: "Date",
  },
];

export const RAW_SQL_BIGINT_RES_TYPES = [
  {
    value: ColumnType.BigInt,
    label: "Big Integer",
  },
];

export const RAW_SQL_JSON_ARR_RES_TYPES = [
  {
    value: ColumnType.JsonArrayNumbers,
    label: "JSON Array (Numbers)",
  },
  {
    value: ColumnType.JsonArrayStrings,
    label: "JSON Array (Strings)",
  },
];

export const LIST_TRAIT_SUPPORTED_WAREHOUSES = [
  "postgres",
  "bigquery",
  "snowflake",
  "redshift",
  "databricks",
  "trino",
  "sample-data",
];

export const PREDICTIVE_TRAIT_SUPPORTED_WAREHOUSES = [
  "bigquery",
  "snowflake",
  "redshift",
  "databricks",
  "athena",
  "postgres",
];

export const InjectedColumnRegexp = /{{\s*"(.*?)"\s*}}/g;

export const getReferencedColumns = (
  formulaTransformation: string,
  parentModel?: {
    filterable_audience_columns: Pick<
      FilterableColumn,
      | "alias"
      | "name"
      | "custom_type"
      | "type"
      | "column_reference"
      | "model_name"
    >[];
    traits: TraitDefinition[];
  },
): FormulaTraitConfig["referencedColumns"] => {
  const referencedColumns: FormulaTraitConfig["referencedColumns"] = [];

  if (formulaTransformation === undefined) {
    return referencedColumns;
  }

  const formattedColumns = transformModelColumnsForFormulaTrait({
    columns: parentModel?.filterable_audience_columns ?? [],
    traits: parentModel?.traits ?? [],
  });

  const matches = formulaTransformation.matchAll(InjectedColumnRegexp);
  for (const matchReference of matches) {
    const injectedAlias = matchReference?.[1];
    if (!injectedAlias) {
      continue;
    }

    // Match alias against formatted columns to resolve the column reference.
    // Checks the full `aliases` array so traits saved when modelId was a
    // string (or vice versa) still resolve under the alternate hash.
    const matchedColumn = formattedColumns.find(({ aliases }) =>
      aliases.includes(injectedAlias),
    );
    if (!matchedColumn) {
      continue;
    }

    referencedColumns.push({
      alias: injectedAlias,
      column: matchedColumn.columnReference,
    });
  }

  return uniqWith(referencedColumns, isEqual);
};

const hasEmptyConditions = (config: TraitConfig) => {
  return config.conditions?.every(
    (condition) => condition.conditions.length === 0,
  );
};

export const parseTraitConfig = (
  type: TraitType,
  rawConfig: TraitConfig,
): {
  aggregatedColumns: (RawColumn | RelatedColumn)[];
  orderByColumn?: RawColumn | RelatedColumn;
  orderByDirection?: string;
  aggregation?: string;
  transformation?: string;
  conditions?: TraitCondition[];
} => {
  switch (type) {
    case TraitType.Sum: {
      const config = rawConfig as SumTraitConfig;
      return {
        aggregatedColumns: [config.column],
        conditions: config.conditions,
      };
    }
    case TraitType.Average: {
      const config = rawConfig as SumTraitConfig;
      return {
        aggregatedColumns: [config.column],
        conditions: config.conditions,
      };
    }
    case TraitType.Count: {
      const config = rawConfig as CountTraitConfig;
      return {
        aggregatedColumns: config.column ? [config.column] : [],
        conditions: config.conditions,
      };
    }
    case TraitType.MostFrequent:
    case TraitType.LeastFrequent: {
      const config = rawConfig as CountDedupedTraitConfig;
      return {
        aggregatedColumns: [config.toSelect],
        conditions: config.conditions,
      };
    }
    case TraitType.First:
    case TraitType.Last: {
      const config = rawConfig as OrderDedupedTraitConfig;
      return {
        aggregatedColumns: [config.toSelect],
        orderByColumn: config.orderBy,
        conditions: config.conditions,
      };
    }
    case TraitType.RawSql: {
      const config = rawConfig as RawSqlTraitConfig;
      return {
        aggregatedColumns: [],
        aggregation: config.aggregation,
        conditions: config.conditions,
      };
    }
    case TraitType.Formula: {
      const config = rawConfig as FormulaTraitConfig;
      return {
        aggregatedColumns: [],
        aggregation: config.transformation,
      };
    }
    case TraitType.Predictive:
    case TraitType.TrackedAudienceMembership:
    case TraitType.RealtimeFormula: {
      return {
        aggregatedColumns: [],
      };
    }
    case TraitType.List: {
      const config = rawConfig as ListTraitConfig;
      return {
        aggregatedColumns:
          config.toSelectList ?? (config.toSelect ? [config.toSelect] : []),
        orderByColumn: config.orderBy?.column,
        orderByDirection: config.orderBy?.direction,
        conditions: config.conditions,
        // TODO: add slice info
      };
    }
    default:
      throw new Error(`Unknown type: ${type satisfies never}`);
  }
};

const isListTraitConfig = (x: TraitConfig): x is ListTraitConfig =>
  ("toSelect" in x && !!x.toSelect) ||
  ("toSelectList" in x && !!x.toSelectList);

export const formatTraitConfig = (
  type: TraitType,
  config: TraitConfig,
  parentModel?: {
    filterable_audience_columns: FilterableColumn[];
    traits: TraitDefinition[];
  },
): TraitConfig => {
  if (type === TraitType.Formula) {
    const formulaConfig = config as FormulaTraitConfig;
    return {
      ...formulaConfig,
      referencedColumns: getReferencedColumns(
        formulaConfig.transformation,
        parentModel,
      ),
    };
  }

  // If the trait is a List trait, infer the resultingType from the column type(s)
  if (type === TraitType.List && parentModel && isListTraitConfig(config)) {
    const { slice, singleObject, ...rest } = config;
    const isMultiColumn = config.toSelectList && config.toSelectList.length > 1;

    // singleObject is only meaningful with a list limit of 1. The form can
    // hold a stale `true` after "Limit list length" is unchecked, so normalize
    // before inferring resultingType — otherwise we'd save scalar metadata for
    // a config whose SQL compiles to an array aggregation, and audience
    // conditions would apply scalar operators (e.g. DATE_TRUNC) to an array.
    const normalizedSingleObject = slice?.maxResults === 1 && !!singleObject;

    let resultingType: ColumnType;

    if (normalizedSingleObject) {
      // When singleObject is enabled, return scalar type (single column) or Json (multi-column object)
      if (isMultiColumn) {
        resultingType = ColumnType.Json;
      } else {
        // Get the raw column type for single-column traits
        const columnReference = config.toSelect;
        const column = parentModel.filterable_audience_columns?.find(
          ({ column_reference }) =>
            isColumnReferenceEqual(column_reference, columnReference),
        );
        resultingType =
          ((column?.custom_type ?? column?.type) as ColumnType) ??
          ColumnType.Unknown;
      }
    } else {
      // Standard array mode - use existing logic
      const trait = { type: TraitType.List, config } as TraitDefinition;
      resultingType = getTraitPropertyType(
        trait,
        parentModel.filterable_audience_columns,
      );
    }

    // Clean up formData, to remove slice, if the user did not select maxResults
    return {
      ...rest,
      singleObject: normalizedSingleObject,
      slice: slice?.location && slice.maxResults ? slice : undefined,
      resultingType,
    };
  }

  if (hasEmptyConditions(config)) {
    return {
      ...config,
      conditions: [],
    };
  }

  return config;
};

const hashFn = (input: string): string => {
  return sha256.create().update(input).hex();
};

export const getColumnAlias = (columnReference: Column) => {
  return getColumnAliasBase(columnReference, hashFn);
};

// Saved formula aliases hashed under one modelId shape (number ↔ string) can
// resolve to the same column under the other alias.
const getSwappedModelIdType = <T extends { modelId: string | number }>(
  col: T,
): T => {
  const { modelId } = col;
  if (typeof modelId === "string" && isNumericString(modelId)) {
    return { ...col, modelId: Number(modelId) };
  }
  if (typeof modelId === "number") {
    return { ...col, modelId: String(modelId) };
  }
  return col;
};

// Returns the alias for a column reference, and an alias for the
// alternative modelId type (number ↔ string)
const getColumnAliases = (
  columnReference: Column,
): [string] | [string, string] => {
  const alias = getColumnAlias(columnReference);
  const aliases: [string] | [string, string] = [alias];

  if (columnReference.type === "raw") {
    const swappedColumnReference = getSwappedModelIdType(columnReference);
    if (swappedColumnReference !== columnReference) {
      aliases.push(getColumnAlias(swappedColumnReference));
    }

    return aliases;
  }

  if (
    columnReference.type === "related" &&
    columnReference.column.type === "raw"
  ) {
    const swappedColumnReference = getSwappedModelIdType(
      columnReference.column,
    );
    if (swappedColumnReference !== columnReference.column) {
      aliases.push(
        getColumnAlias({
          ...columnReference,
          column: swappedColumnReference,
        }),
      );
    }

    return aliases;
  }

  return aliases;
};

export type FormulaColumnAutocompletion = Completion & {
  // Custom fields needed to decorate the edtior's Pill and referenced column resolution
  alias: string;
  aliases: string[];
  columnReference: Column;
  isParent: boolean;
  isMerged: boolean;
  isTrait: boolean;
};

export const formulaCompletionSection = (name: string): CompletionSection => ({
  name,
  header: () => {
    const el = document.createElement("div");
    el.setAttribute(
      "style",
      "padding: 8px; font-weight: var(--chakra-fontWeights-medium)",
    );
    el.appendChild(document.createTextNode(name));
    return el;
  },
});

export const transformModelColumnsForFormulaTrait = (args: {
  columns: Pick<
    FilterableColumn,
    | "alias"
    | "name"
    | "custom_type"
    | "type"
    | "column_reference"
    | "model_name"
  >[];
  traits: TraitDefinition[];
}): FormulaColumnAutocompletion[] => {
  const { columns, traits } = args;

  const formattedColumns: {
    name: string;
    type: string;
    columnReference: Column;
    modelName?: string;
    isParent: boolean;
    isMerged: boolean;
    isTrait: boolean;
  }[] = [
    ...columns.map((c) => ({
      name: c.alias ?? c.name,
      type: c.custom_type ?? c.type,
      columnReference: c.column_reference as Column,
      modelName: c.model_name,
      isParent: !isMergedColumn(c.column_reference),
      isMerged: isMergedColumn(c.column_reference),
      isTrait: false,
    })),
    // Filter out transformed columns because they can't be referenced within a formula trait.
    // Then convert the trait definition into a related column definition.
    ...traits
      .filter((t) => t.type !== TraitType.Formula)
      .map((t) => ({
        name: t.name,
        type: getTraitPropertyType(
          t,
          t.relationship?.to_model.filterable_audience_columns,
        ),
        columnReference: transformTraitColumnToRelatedColumn({
          ...t,
          // Thee traits should have empty conditions. We do some transformations
          // specifically for the audience builder, but the backend will correctly apply
          // the conditions since we look up the trait.
          // Setting non-empty conditions here is also prone to staleness since we
          // persist these colunns and hash them.
          config: { ...t.config, conditions: [] },
        }),
        isParent: false,
        isMerged: false,
        isTrait: true,
      })),
  ];

  return formattedColumns.map(
    ({
      columnReference,
      name,
      type,
      modelName,
      isParent,
      isMerged,
      isTrait,
    }): FormulaColumnAutocompletion => {
      const aliases = getColumnAliases(columnReference);
      const alias = aliases[0];
      return {
        // CodeMirror autocomplete will filter out duplicate labels, so they won't all show up in the suggetions.
        // We can get into this scenario if there is a merged column with the same name as a parent model column.
        // We'll dedupe them by sufficing the label. This doesn't look great aesthetically, so we should find
        // a better workaround if possible.
        label: isMerged ? `${name} (${modelName})` : name,
        // This is the text that will be injected upon selecting an autocompletion option
        // but will be hidden from the user behind a pill. The injected text is Handlebars syntax which
        // our backend SQL generation code understands.
        apply: `{{"${alias}"}}`,
        alias,
        aliases,
        columnReference,
        type,
        isParent,
        isMerged,
        isTrait,
        section: formulaCompletionSection(
          isTrait ? "Traits" : isMerged ? "Merged columns" : "Parent model",
        ),
      };
    },
  );
};

// TODO(samuel): remove this function once audience builder is upgraded to use 'yup'.
// Have to do this to trigger the validation in the trait conditions
export const validateTraitConfig = async (
  hasValidationErrorsFn: () => boolean,
  toast: (options: any) => void,
) => {
  const validationErrors = hasValidationErrorsFn();

  if (validationErrors) {
    toast({
      id: "trait-validation-error",
      title: "Trait has validation errors",
      message: "Please fix the errors and try again.",
      variant: "error",
    });

    return Promise.reject();
  }

  return Promise.resolve();
};

export const DUPLICATE_TRAIT_NAME_ERROR_MESSAGE =
  "There is an existing trait with this name associated with this parent model. Please choose a different name and try again.";

// The unique constraint on trait_definitions (name, segment_id, journey_id).
// Postgres unique-violation messages name the violated constraint.
const TRAIT_NAME_UNIQUE_CONSTRAINT =
  "trait_definitions_name_segment_id_journey_id_key";

export const isDuplicateTraitNameError = (error: Error): boolean =>
  error.message.includes(TRAIT_NAME_UNIQUE_CONSTRAINT);

export const traitNameValidator = (
  connectionSourceType: string | undefined,
) => {
  const shared = Yup.string().required("Trait name required");

  // Note: trait names themselves are not as important as the sync column target names.
  // We validate here, to reduce chance for an error downstream, when people copy the trait names
  // in setting up the destination columns.
  switch (connectionSourceType) {
    case "bigquery":
      return shared.test(
        "validate-trait-name-characters",
        "BigQuery trait names can only contain letters, numbers, spaces, and underscores.",
        (value) => {
          return !value?.match(bigqueryDisallowedIdentifierCharactersRegex);
        },
      );
    case "databricks":
      return shared.test(
        "validate-trait-name-characters",
        "Databricks trait names must not contain any of the following characters ' ,;{}()\\n\\t='",
        (value) => {
          return !value?.match(/[ ,;{}()\n\t=]/g);
        },
      );
    default:
      return shared;
  }
};

// Number types cause some issue in the current Databricks and Redshift implementations of List Trait (Listagg rather than Array_agg)
// The below constant is used to filter out these colunn types from being used in those DWH
export const NUMBER_TYPES = [ColumnType.Number, ColumnType.BigInt];

// Some warehouses report arbitrary-precision numerics as strings to avoid
// float64 precision loss (e.g. Postgres NUMERIC and int8, Databricks
// DECIMAL), so the mapped ColumnType alone misses natively summable columns
// — the raw warehouse type is the tiebreaker. Postgres stores it as the
// type OID (int8, money, numeric); other warehouses store the type name.
const PG_NUMERIC_TYPE_OIDS = new Set(["20", "790", "1700"]);
const NUMERIC_RAW_TYPE_REGEX =
  /\b(numeric|decimal|number|money|bigint|int8)\b/i;

export const isNumericRawType = (rawType: string | null | undefined): boolean =>
  rawType != null &&
  (PG_NUMERIC_TYPE_OIDS.has(rawType.trim()) ||
    NUMERIC_RAW_TYPE_REGEX.test(rawType));

export const sourceSupportsNumberListTraits = (source: string) => {
  // Of the sources we support in LIST_TRAIT_SUPPORTED_WAREHOUSES,
  // Some do not allow number list traits, since they result in string outputs
  const NUMBER_LIST_TRAITS_NOT_SUPPORTED = ["redshift"];
  return !NUMBER_LIST_TRAITS_NOT_SUPPORTED.includes(source);
};

export const getColumnDisabledMessageForListTraits = (source?: string) => {
  return `Number types not yet supported in list traits for ${capitalize(source)}`;
};
